import { collection, getDocs } from "firebase/firestore";
import { ref, getDownloadURL } from "firebase/storage";
import { db, storage } from "../../../../firebase";
import { getAuth } from "firebase/auth";

/**
 * Fetches top-level folders for the authenticated user.
 * Returns an array of folder objects with folderId, folderName, and status.
 */
export async function fetchUserFolders() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }
    const userId = user.uid;

    const foldersSnapshot = await getDocs(
      collection(db, `users/${userId}/folders`)
    );

    const folders = foldersSnapshot.docs.map((doc) => ({
      folderId: doc.id,
      ...doc.data(), // includes all folder fields (status, adminNotes, timestamps, etc.)
    }));

    return folders;
  } catch (error) {
    console.error("Error fetching user folders:", error);
    return [];
  }
}
/**
 * Fetches subfolders for a given folderId for the authenticated user.
 * Returns an array of subfolder objects with id and subfolderName.
 */
export async function fetchUserSubfolders(folderId) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }
    const userId = user.uid;

    const subfoldersSnapshot = await getDocs(
      collection(db, `users/${userId}/folders/${folderId}/subfolders`)
    );

    const subfolders = subfoldersSnapshot.docs.map((doc) => {
      const data = doc.data();
      return {
        id: doc.id,
        subfolderName: data.subfolderName || "Unnamed Subfolder",
      };
    });

    return subfolders;
  } catch (error) {
    console.error("Error fetching user subfolders:", error);
    return [];
  }
}

/**
 * Fetches files for a given folderId and subfolderId for the authenticated user.
 * Returns an array of file objects with id, file data, and downloadURL.
 */
export async function fetchUserFiles(folderId, subfolderId) {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }
    const userId = user.uid;

    const filesSnapshot = await getDocs(
      collection(
        db,
        `users/${userId}/folders/${folderId}/subfolders/${subfolderId}/files`
      )
    );

    const files = [];

    for (const fileDoc of filesSnapshot.docs) {
      const fileId = fileDoc.id;
      const fileData = fileDoc.data();
      const fileName = fileData.fileName || "Unnamed File";

      // Construct storage path
      const storagePath = `users/${userId}/folders/${folderId}/${fileData.subfolderName}/${fileName}`;

      let downloadURL = "";
      try {
        const fileRef = ref(storage, storagePath);
        downloadURL = await getDownloadURL(fileRef);
      } catch (err) {
        console.warn("Failed to get download URL for", fileName, err);
      }

      files.push({
        id: fileId,
        ...fileData,
        downloadURL,
      });
    }

    return files;
  } catch (error) {
    console.error("Error fetching user files:", error);
    return [];
  }
}

/**
 * Fetches all files across all folders and subfolders for the authenticated user.
 * Returns an array of file objects with id, file data, and downloadURL.
 */
export async function fetchAllUserFiles() {
  try {
    const auth = getAuth();
    const user = auth.currentUser;
    if (!user) {
      throw new Error("User not authenticated");
    }
    const userId = user.uid;

    const foldersSnapshot = await getDocs(
      collection(db, `users/${userId}/folders`)
    );

    const allFiles = [];

    for (const folderDoc of foldersSnapshot.docs) {
      const folderId = folderDoc.id;

      const subfoldersSnapshot = await getDocs(
        collection(db, `users/${userId}/folders/${folderId}/subfolders`)
      );

      for (const subfolderDoc of subfoldersSnapshot.docs) {
        const subfolderId = subfolderDoc.id;
        const subfolderData = subfolderDoc.data();
        const subfolderName =
          subfolderData.subfolderName || "Unnamed Subfolder";

        const filesSnapshot = await getDocs(
          collection(
            db,
            `users/${userId}/folders/${folderId}/subfolders/${subfolderId}/files`
          )
        );

        for (const fileDoc of filesSnapshot.docs) {
          const fileId = fileDoc.id;
          const fileData = fileDoc.data();
          const fileName = fileData.fileName || "Unnamed File";

          // Construct storage path
          const storagePath = `users/${userId}/folders/${folderId}/${subfolderName}/${fileName}`;

          let downloadURL = "";
          try {
            const fileRef = ref(storage, storagePath);
            downloadURL = await getDownloadURL(fileRef);
          } catch (err) {
            console.warn("Failed to get download URL for", fileName, err);
          }

          allFiles.push({
            id: fileId,
            ...fileData,
            downloadURL,
          });
        }
      }
    }

    return allFiles;
  } catch (error) {
    console.error("Error fetching all user files:", error);
    return [];
  }
}
